import { drawLeafShape, type LeafKind } from "../BackgroundAnimations/leafShapes";
import { PRIDE_STRIPES_COUNT, prideStripeColor } from "../BackgroundAnimations/prideStripes";
import { strokeSnowflakeSpokes } from "../BackgroundAnimations/snowflakeShapes";
import { BALL_TYPES, type BallType, traceBallBody, traceBallMarks } from "../BackgroundAnimations/sportsBallShapes";

export type ThemedBurstType = "light" | "ball" | "snowflake" | "leaf" | "confetti";

export interface BurstOrigin {
  x: number;
  y: number;
  rect: { left: number; bottom: number; width: number };
}

type ColorFn = (alpha: number) => string;

interface ParticleBase {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  rotV: number;
  wob: number;
  wobAmp: number;
  life: number;
  maxLife: number;
  fadeStart: number;
}

interface LightParticle extends ParticleBase {
  type: "light";
  size: number;
  swayAmplitude: number;
  swaySpeed: number;
  flicker: number;
  flickerSpeed: number;
}

interface BallParticle extends ParticleBase {
  type: "ball";
  ballType: BallType;
  size: number;
  outlineWidth: number;
  marksWidth: number;
  floor: number;
  bounces: number;
}

interface SnowflakeParticle extends ParticleBase {
  type: "snowflake";
  size: number;
}

interface LeafParticle extends ParticleBase {
  type: "leaf";
  leafKind: LeafKind;
  size: number;
  flipSpeed: number;
  flipPhase: number;
}

interface ConfettiParticle extends ParticleBase {
  type: "confetti";
  colorIndex: number;
  width: number;
  height: number;
  flipSpeed: number;
  flipPhase: number;
}

export type ThemedBurstParticle = LightParticle | BallParticle | SnowflakeParticle | LeafParticle | ConfettiParticle;

export const THEMED_BURST_COUNT: Record<ThemedBurstType, number> = {
  light: 5,
  ball: 4,
  snowflake: 7,
  leaf: 6,
  confetti: 16,
};

const BURST_BALL_SCALE: Record<BallType, number> = {
  golf: 0.7,
  tennis: 0.78,
  badminton: 0.8,
  baseball: 0.8,
  volleyball: 1,
  soccer: 1,
  basketball: 1.05,
  football: 0.92,
};
const BURST_LEAF_SCALE = 0.8;
const SNOWFLAKE_MIN_SPREAD_WIDTH = 60;
const PEAK_ALPHA = 0.92;

const rand = (min: number, max: number): number => min + Math.random() * (max - min);
const upwardAngle = (spread: number): number => -Math.PI / 2 + (Math.random() - 0.5) * 2 * spread;

function baseParticle(x: number, y: number): ParticleBase {
  return {
    x,
    y,
    vx: 0,
    vy: 0,
    rot: 0,
    rotV: 0,
    wob: Math.random() * Math.PI * 2,
    wobAmp: rand(0.6, 1.2),
    life: -Math.floor(Math.random() * 12),
    maxLife: rand(70, 130),
    fadeStart: rand(0.62, 0.84),
  };
}

function createLight({ x, y }: BurstOrigin): LightParticle {
  return {
    ...baseParticle(x + rand(-22, 22), y + rand(-6, 6)),
    type: "light",
    vy: -(0.12 + Math.random() * 0.26),
    size: 2.2 + Math.random() * 3.6,
    swayAmplitude: 0.3 + Math.random() * 0.9,
    swaySpeed: 0.006 + Math.random() * 0.014,
    flicker: Math.random() * Math.PI * 2,
    flickerSpeed: 0.03 + Math.random() * 0.05,
    life: -Math.floor(Math.random() * 20),
    maxLife: rand(200, 300),
  };
}

function createBall({ x, y }: BurstOrigin): BallParticle {
  const ballType = BALL_TYPES[Math.floor(Math.random() * BALL_TYPES.length)];
  const angle = upwardAngle(0.75);
  const speed = rand(3.4, 4.8);
  const vx = Math.cos(angle) * speed;
  const size = rand(10, 13) * BURST_BALL_SCALE[ballType];
  return {
    ...baseParticle(x + rand(-8, 8), y),
    type: "ball",
    ballType,
    size,
    outlineWidth: Math.max(1.5, size * 0.14),
    marksWidth: Math.max(1.1, size * 0.1),
    vx,
    vy: Math.sin(angle) * speed,
    rot: Math.random() * Math.PI * 2,
    rotV: vx * 0.035,
    floor: y + rand(26, 40),
    bounces: 0,
    life: -Math.floor(Math.random() * 8),
    maxLife: rand(70, 95),
    fadeStart: 0.7,
  };
}

function createSnowflake({ rect }: BurstOrigin): SnowflakeParticle {
  const spreadWidth = Math.max(rect.width, SNOWFLAKE_MIN_SPREAD_WIDTH);
  const spreadLeft = rect.left + (rect.width - spreadWidth) / 2;
  return {
    ...baseParticle(spreadLeft + rand(0.1, 0.9) * spreadWidth, rect.bottom + rand(-3, 2)),
    type: "snowflake",
    size: rand(4, 8.5),
    vx: rand(-0.25, 0.25),
    vy: rand(0, 0.25),
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.03,
    life: -Math.floor(Math.random() * 24),
    maxLife: rand(130, 190),
    fadeStart: rand(0.55, 0.75),
  };
}

function createLeaf({ x, y }: BurstOrigin): LeafParticle {
  const leafKind: LeafKind = Math.random() < 0.6 ? "maple" : "ovate";
  const angle = upwardAngle(0.9);
  const speed = rand(2.2, 3.6);
  return {
    ...baseParticle(x + rand(-8, 8), y),
    type: "leaf",
    leafKind,
    size: (8 + Math.random() * 9) * (leafKind === "maple" ? 1.44 : 1) * BURST_LEAF_SCALE,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.06,
    flipSpeed: rand(0.06, 0.11),
    flipPhase: Math.random() * Math.PI * 2,
    maxLife: rand(120, 170),
  };
}

function createConfetti({ x, y }: BurstOrigin): ConfettiParticle {
  const angle = upwardAngle(0.95);
  const speed = rand(3, 5.6);
  return {
    ...baseParticle(x + rand(-6, 6), y),
    type: "confetti",
    colorIndex: Math.floor(Math.random() * PRIDE_STRIPES_COUNT),
    width: (6 + Math.random() * 7) * 1.2,
    height: (3 + Math.random() * 4) * 1.2,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.25,
    flipSpeed: rand(0.08, 0.16),
    flipPhase: Math.random() * Math.PI * 2,
    life: -Math.floor(Math.random() * 4),
    maxLife: rand(80, 120),
  };
}

export function createThemedParticle(type: ThemedBurstType, origin: BurstOrigin): ThemedBurstParticle {
  switch (type) {
    case "light":
      return createLight(origin);
    case "ball":
      return createBall(origin);
    case "snowflake":
      return createSnowflake(origin);
    case "leaf":
      return createLeaf(origin);
    case "confetti":
      return createConfetti(origin);
  }
}

export function advanceThemedParticle(p: ThemedBurstParticle): void {
  p.life++;
  switch (p.type) {
    case "light":
      p.x += Math.sin(p.life * p.swaySpeed + p.wob) * p.swayAmplitude * 0.25;
      p.y += p.vy;
      return;
    case "ball":
      p.vy += 0.11;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.rotV;
      if (p.bounces < 1 && p.vy > 0 && p.y > p.floor) {
        p.y = p.floor;
        p.vy *= -0.5;
        p.vx *= 0.8;
        p.rotV *= 0.6;
        p.bounces++;
      }
      return;
    case "snowflake":
      p.vx *= 0.98;
      p.vy = p.vy * 0.97 + 0.018;
      p.x += p.vx + Math.sin(p.life * 0.05 + p.wob) * 0.35 * p.wobAmp;
      p.y += p.vy;
      p.rot += p.rotV * (1 + Math.abs(p.vx));
      return;
    case "leaf":
      p.vx *= 0.96;
      p.vy = p.vy * 0.96 + 0.045;
      p.x += p.vx + Math.sin(p.life * 0.06 + p.wob) * 0.6 * p.wobAmp;
      p.y += p.vy;
      p.rot += p.rotV + p.vx * 0.01;
      return;
    case "confetti":
      p.vx *= 0.965;
      p.vy = p.vy * 0.965 + 0.07;
      p.x += p.vx + Math.sin(p.life * 0.1 + p.wob) * 0.3;
      p.y += p.vy;
      p.rot += p.rotV;
      return;
  }
}

function alphaFor(p: ThemedBurstParticle, t: number): number {
  if (t < 0.1) {
    return (t / 0.1) * PEAK_ALPHA;
  }
  if (t > p.fadeStart) {
    const fade = (1 - t) / (1 - p.fadeStart);
    return fade * fade * PEAK_ALPHA;
  }
  return PEAK_ALPHA;
}

function popScale(t: number): number {
  if (t < 0.1) {
    return (t / 0.1) * 1.18;
  }
  if (t < 0.18) {
    return 1.18 - 0.18 * ((t - 0.1) / 0.08);
  }
  return 1;
}

function drawLight(ctx: CanvasRenderingContext2D, tc: ColorFn, p: LightParticle, t: number): void {
  const alpha = alphaFor(p, t) * (0.75 + 0.25 * Math.sin(p.life * p.flickerSpeed + p.flicker));
  const scale = popScale(t);
  const haloRadius = p.size * 5 * scale;
  const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloRadius);
  halo.addColorStop(0, tc(alpha * 0.55));
  halo.addColorStop(0.5, tc(alpha * 0.16));
  halo.addColorStop(1, tc(0));
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(p.x, p.y, haloRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = tc(alpha);
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size * 0.75 * scale, 0, Math.PI * 2);
  ctx.fill();
}

function drawBall(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BallParticle, t: number): void {
  const alpha = alphaFor(p, t);
  const scale = popScale(t);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.ballType === "badminton" ? Math.atan2(p.vy, p.vx) - Math.PI / 2 : p.rot);
  ctx.scale(scale, scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.fillStyle = tc(alpha * 0.14);
  traceBallBody(ctx, p.ballType, p.size);
  ctx.fill();
  ctx.strokeStyle = tc(alpha);
  ctx.fillStyle = tc(alpha * 0.55);
  ctx.lineWidth = p.marksWidth;
  traceBallMarks(ctx, p.ballType, p.size);
  ctx.lineWidth = p.outlineWidth;
  traceBallBody(ctx, p.ballType, p.size);
  ctx.stroke();
  ctx.restore();
}

function drawSnowflake(ctx: CanvasRenderingContext2D, tc: ColorFn, p: SnowflakeParticle, t: number): void {
  const alpha = alphaFor(p, t);
  ctx.save();
  ctx.translate(p.x, p.y);
  const glowRadius = p.size * 2.4;
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
  glow.addColorStop(0, tc(alpha * 0.35));
  glow.addColorStop(1, tc(0));
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.rotate(p.rot);
  ctx.strokeStyle = tc(alpha);
  strokeSnowflakeSpokes(ctx, p.size);
  ctx.restore();
}

function drawLeaf(ctx: CanvasRenderingContext2D, tc: ColorFn, p: LeafParticle, t: number): void {
  const scale = popScale(t);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale * Math.cos(p.life * p.flipSpeed + p.flipPhase));
  drawLeafShape(ctx, tc, p.leafKind, p.size, alphaFor(p, t));
  ctx.restore();
}

function drawConfetti(ctx: CanvasRenderingContext2D, p: ConfettiParticle, t: number): void {
  const scale = popScale(t);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(Math.cos(p.life * p.flipSpeed + p.flipPhase) * scale, scale);
  ctx.fillStyle = prideStripeColor(p.colorIndex, alphaFor(p, t) * 0.9);
  ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
  ctx.restore();
}

export function drawThemedParticle(ctx: CanvasRenderingContext2D, tc: ColorFn, p: ThemedBurstParticle): void {
  const t = p.life / p.maxLife;
  switch (p.type) {
    case "light":
      return drawLight(ctx, tc, p, t);
    case "ball":
      return drawBall(ctx, tc, p, t);
    case "snowflake":
      return drawSnowflake(ctx, tc, p, t);
    case "leaf":
      return drawLeaf(ctx, tc, p, t);
    case "confetti":
      return drawConfetti(ctx, p, t);
  }
}
